import { isLightColor } from "@design-lab/shared/lib/color";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import type { Color } from "@design-lab/shared/types/base";
import { Button } from "@design-lab/shared/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@design-lab/shared/ui/card";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

type ColorPaletteProps = {
  recommendedColors: Color[];
  otherColors: Color[];
  onColorSelect: (id: string) => void;
  selectedColor: Color;
  showToggleButton?: boolean;
  showAllColors?: boolean;
  onToggleShowAll?: (show: boolean) => void;
  showRecommendedToggle?: boolean;
  showAllRecommended?: boolean;
  onToggleShowAllRecommended?: (show: boolean) => void;
  maxColors?: number;
  onColorHover?: (id: string) => void;
  onColorBlur?: () => void;
};

export function ColorPalette(props: ColorPaletteProps) {
  const {
    recommendedColors,
    otherColors,
    onColorSelect,
    selectedColor,
    showToggleButton = false,
    showAllColors = false,
    onToggleShowAll = undefined,
    showRecommendedToggle = false,
    showAllRecommended = false,
    onToggleShowAllRecommended = undefined,
    maxColors = 24,
    onColorHover,
    onColorBlur
  } = props;
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const handleImageError = (colorId: string) => {
    setImageErrors(prev => new Set(prev).add(colorId));
  };

  const colorGroupsArray = [
    {
      title: "Fast Delivery",
      colors: showAllRecommended ? recommendedColors : recommendedColors.slice(0, maxColors),
      showAll: showAllRecommended,
      showToggleButton: showRecommendedToggle,
      onToggle: onToggleShowAllRecommended,
      maxVisible: maxColors,
      testId: "toggle-show-all-recommended-button",
      bgColor: "bg-lime-300",
      borderColor: "border-lime-400"
    },
    {
      title: recommendedColors?.length ? "All Other Colors" : "All Colors",
      colors: showAllColors ? otherColors : otherColors.slice(0, maxColors),
      showAll: showAllColors,
      showToggleButton,
      onToggle: onToggleShowAll,
      maxVisible: maxColors,
      testId: "toggle-show-all-button",
      bgColor: "bg-gray-200",
      borderColor: "border-gray-200"
    }
  ];

  return (
    <div className="font-inter space-y-2" onMouseLeave={onColorBlur}>
      {colorGroupsArray.map(
        ({
          title,
          colors,
          showAll,
          showToggleButton: showSectionToggle,
          onToggle,
          maxVisible,
          testId,
          bgColor,
          borderColor
        }) => {
          // Hide section if no colors
          if (colors.length === 0) return null;

          return (
            <Card key={title} className={cn("border-1 flex flex-col", borderColor)}>
              <CardHeader className={cn("rounded-t-lg border-b p-0", bgColor)}>
                <CardTitle className="mb-0 p-1 text-center text-[12px]">{title}</CardTitle>
              </CardHeader>
              <CardContent className="flex h-full flex-1 flex-col space-y-4 p-3">
                <div className="m-0 mb-2 grid grid-cols-[repeat(auto-fill,minmax(2rem,1fr))] gap-2">
                  {colors.map(color => (
                    <TooltipTrigger key={color.id} delay={0}>
                      <Button
                        data-testid={`color-${color.id}-button`}
                        onPress={() => !color.outOfStock && onColorSelect(color.id)}
                        onHoverStart={() => onColorHover?.(color.id)}
                        className={cn(
                          "group relative size-8 p-0",
                          "bg-background z-[1] box-border overflow-hidden rounded-full border border-gray-300",
                          "focus:ring-foreground focus:outline-none focus:ring-2 focus:ring-offset-2",
                          "transition-all duration-200",
                          color.outOfStock ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                          selectedColor.id === color.id && "ring-foreground ring-2 ring-offset-2",
                          selectedColor.id !== color.id &&
                            !color.outOfStock &&
                            "hover:ring-2 hover:ring-gray-200 hover:ring-offset-2"
                        )}
                        aria-label={
                          color.outOfStock
                            ? `${color.name} - Out of Stock`
                            : `Select ${color.name} color`
                        }
                      >
                        <span className="sr-only">{color.name}</span>
                        {color.swatchUrl && !imageErrors.has(color.id) ? (
                          <img
                            src={color.swatchUrl}
                            alt={color.name}
                            className="size-full"
                            onError={() => handleImageError(color.id)}
                          />
                        ) : (
                          <span
                            className="block size-full rounded-full"
                            style={{ backgroundColor: color.hex }}
                          />
                        )}
                        {color.outOfStock && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="h-0.5 w-full rotate-45 bg-white shadow-sm" />
                          </span>
                        )}
                        {selectedColor.id === color.id && !color.outOfStock && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Check
                              className={cn(
                                "size-4",
                                isLightColor(color.hex) ? "text-foreground" : "text-background"
                              )}
                            />
                          </div>
                        )}
                      </Button>
                      <Tooltip placement="top" offset={12}>
                        {color.outOfStock ? `${color.name} - Out of Stock` : color.name}
                      </Tooltip>
                    </TooltipTrigger>
                  ))}
                </div>

                {showSectionToggle &&
                  onToggle &&
                  (title === "Fast Delivery"
                    ? recommendedColors.length > maxVisible
                    : otherColors.length > maxVisible) && (
                    <Button
                      data-testid={testId}
                      variant="secondary"
                      className="w-full font-bold"
                      onPress={() => onToggle(!showAll)}
                    >
                      {showAll ? "Show Less" : "Show All"}
                      {showAll ? (
                        <ChevronUp className="ml-1 size-4" />
                      ) : (
                        <ChevronDown className="ml-1 size-4" />
                      )}
                    </Button>
                  )}
              </CardContent>
            </Card>
          );
        }
      )}
    </div>
  );
}
